@if (legacyFormService.form.has_invalid_fields) {
  <div class="message-box alert">
    <span class="icon" inlineSVG="/assets/icons/attention.svg"></span>
    <p class="message">This form has fields without variable assignments</p>
  </div>
}

@if (formHasNonGlobalVariables$ | async) {
  <div class="message-box alert">
    <span class="icon" inlineSVG="/assets/icons/attention.svg"></span>
    <p class="message">
      This form has custom variables that belong to a non-global organization.
      <button (click)="repairCustomVariables()">Repair Custom Variables</button>
    </p>
  </div>
}

<div cdkDropList (cdkDropListDropped)="dropSection($event)">
  <mat-card
    appearance="outlined"
    *ngFor="let section of legacyFormService.form.sections; index as sIndex;"
    cdkDrag
    class="mat-card__section fl-builder-form-section component-selectable"
    type="section"
    [attr.data-id]="section.id"
    [attr.data-selectable-type]="'section'"
    [class.selected]="isObjectSelected(section)"
    [id]="section.id"
  >
    <header>
      <div class="drag-handle" cdkDragHandle>
        <i class="material-icons" *ngIf="!legacyFormService.savingForm && legacyFormService.editable">drag_handle</i>
      </div>

      @if (isElementConditionalVisible(section)) {
        <i class="material-icons conditional-visibility" matTooltip="Section is conditionally visible">
          visibility_off
        </i>
      } @else {
        <i class="material-icons conditional-visibility-always" matTooltip="Section is always visible">
          visibility
        </i>
      }

      <h2 type="section" [id]="section.id" data-testid="section_title">
        {{section.title || 'Untitled Section'}}
      </h2>

      <div class="toolbar-actions" *ngIf="isObjectSelected(section) && legacyFormService.editable">
        <a class="toolbar-action" (click)="moveSectionToTop(section)">
          <i class="material-icons">vertical_align_top</i>
        </a>
        <a class="toolbar-action" (click)="moveSectionTowards(section, -1)">
          <i class="material-icons">keyboard_arrow_up</i>
        </a>
        <a class="toolbar-action" (click)="moveSectionTowards(section, 1)">
          <i class="material-icons">keyboard_arrow_down</i>
        </a>
        <a class="toolbar-action" (click)="moveSectionToBottom(section)">
          <i class="material-icons">vertical_align_bottom</i>
        </a>

        <div class="divider"></div>

        <a *ngIf="!(legacyFormService.form.rtd && sIndex === 0)"
           class="toolbar-action"
           role="delete"
           type="section"
           [id]="section.id"
           (click)="removeSection(section)">
          <i class="material-icons">delete_forever</i>
        </a>
      </div>
    </header>

    <mat-card-content
      cdkDropList
      type="section"
      [id]="section.id"
      [cdkDropListData]="legacyFormService.form.sections[sIndex]"
      (cdkDropListDropped)="dropElement($event)"
      [cdkDropListConnectedTo]="sectionIdRefs"
    >
      <div *ngFor="let element of section.elements; let j = index;"
           class="fl-builder-form-element component-selectable"
           type="element"
           [attr.data-id]="element.id"
           [attr.data-selectable-type]="'element'"
           [attr.data-test-element-type]="element.element_object_type === 'form/field' ? element.element_object.widget_type : element.element_object_type"
           [class.selected]="isObjectSelected(element)"
           [class.invalid]="element.element_object_type === 'form/field' && !element.element_object.custom_variable_key"
           [class.page-object-counter-signable]="isElementCounterSignable(element)"
           [id]="element.id"
           (click)="setElementSectionId(section)"
           cdkDrag>
        <div class="fl-builder-form-element--drag-handle" cdkDragHandle>
          <i class="material-icons" *ngIf="!legacyFormService.savingForm && legacyFormService.editable">drag_handle</i>
        </div>

        <!-- Element Content -->
        <div class="flex-container vertical-center">
          <!-- Element Error State -->
          <i
            *ngIf="element | fieldNotInGlobalOrg"
            class="material-icons error" matTooltip="This field's variable belongs to an outside organization"
          >
            warning
          </i>


          <!-- Element Iconography -->
          <i class="material-icons role {{element.role}}" [matTooltip]="actorRoleIcons[element.role].tooltip">
            {{ actorRoleIcons[element.role].icon }}
          </i>

          <ng-template #conditionalVisibleIcon>
            @if (isElementConditionalVisible(element)) {
              <i class="material-icons conditional-visibility" matTooltip="Element is conditionally visible">
                visibility_off
              </i>
            } @else {
              <i class="material-icons conditional-visibility-always" matTooltip="Element is always visible">
                visibility
              </i>
            }
          </ng-template>

          @if (element.element_object.page_object_id) {
            <span class="grouped-icons">
              <ng-container *ngTemplateOutlet="conditionalVisibleIcon"></ng-container>

              <i class="material-icons document" matTooltip="This field has been mapped to the associated document">
                description
              </i>
            </span>
          } @else {
            <span class="ungrouped-icon">
              <ng-container *ngTemplateOutlet="conditionalVisibleIcon"></ng-container>
            </span>
          }

          @if (isElementFixedText(element)) {
            <i class="material-icons" type="element" matTooltip="Fixed text is only stamped on the associated document, not shown in the form">
              format_shapes
            </i>
          } @else {
            <i class="material-icons" type="element">
              @if (element.element_object_type === 'form/field') {
                {{ WIDGET_TYPES[element.element_object.widget_type].icon }}
              } @else {
                {{ WIDGET_TYPES[element.element_object_type].icon }}
              }
            </i>
          }

          <!-- Element Content/Question/Etc -->
          <div
            class="mat-card-content__element--text element-variable-question"
            [class.question-location-above]="element.configuration.questionLocation === 'above'"
            type="element"
            [id]="element.id"
          >
            <label type="element" [id]="element.id">
              <span class="element-label-title">
                {{ element.element_object.label || element.element_object.title || "Untitled" }}

                @if (isRequired(element)) {
                  *
                }
              </span>
            </label>

            @if (element.element_object.tooltip || element.element_object.raw_html) {
              <div class="divider"></div>

              <div class="question">
                @if (element.element_object_type === 'form/field') {
                  {{ element.element_object.tooltip | htmlToText: 300 }}
                } @else {
                  {{ element.element_object.raw_html | htmlToText: 300 }}
                }
              </div>
            }

            @if (element.element_object_type === 'form/field') {
              <div class="element-meta">
                @if (element.element_object_type === 'form/field' && element.element_object.custom_variable_key) {
                  <span class="badge variable {{element.role}}">
                    {{ element.element_object.custom_variable_key }}
                  </span>
                }

                @if (element.configuration.groupId) {
                  <span class="badge" matTooltip="This element is grouped">
                    {{ element.configuration.groupId }}

                    @if (element.configuration.groupSpan) {
                      | {{ element.configuration.groupSpan }}
                    }
                  </span>
                }
              </div>
            }
          </div>

          <span class="badge" matTooltip="This field has been mapped to the associated RTD." *ngIf="legacyFormService.form.rtd && element.element_object.data_type === 'signature'">RTD Signature</span>

        </div>

        <!-- Element Toolbar Actions -->
        @if (isObjectSelected(element) && legacyFormService.editable) {
          <div class="toolbar-actions">
            <a class="toolbar-action" (click)="moveElementToTop(element, section)">
              <i class="material-icons">vertical_align_top</i>
            </a>
            <a class="toolbar-action" (click)="moveElementTowards(element, section, -1)">
              <i class="material-icons">keyboard_arrow_up</i>
            </a>
            <a class="toolbar-action" (click)="moveElementTowards(element, section, 1)">
              <i class="material-icons">keyboard_arrow_down</i>
            </a>
            <a class="toolbar-action" (click)="moveElementToBottom(element, section)">
              <i class="material-icons">vertical_align_bottom</i>
            </a>

            <div class="divider"></div>

            <a class="toolbar-action action-remove" type="element" role="delete" (click)="removeElement(section, element)">
              <i class="material-icons">delete_forever</i>
            </a>
          </div>
        }
      </div>

      <div class="flex-container vertical-center" *ngIf="section.type === SectionType.InvestmentAccountSelection">
        <i class="material-icons" style="margin-right: 10px">library_add</i>
        <p>This is a special section to select or create an investment profile</p>
      </div>

      <div class="flex-container vertical-center" *ngIf="section.type === SectionType.InvestmentAccountSummary">
        <i class="material-icons" style="margin-right: 10px">add_task</i>
        <p>This is a special section to confirm the selected investment account</p>
      </div>

      <div class="flex-container vertical-center" *ngIf="section.type === SectionType.Investors">
        <i class="material-icons" style="margin-right: 10px">group</i>
        <p>This is a special section to add investors to a deal.</p>
      </div>

      <div class="flex-container vertical-center" *ngIf="section.type === SectionType.Team">
        <i class="material-icons" style="margin-right: 10px">group</i>
        <p>This is a special section to add team members from a form step</p>
      </div>

      <div class="flex-container vertical-center" *ngIf="section.type === SectionType.ProviderAccessRequest">
        <i class="material-icons" style="margin-right: 10px">groups</i>
        <p>This is a special section that allows investors to grant access to GPs</p>
      </div>
    </mat-card-content>

    <div class="card-footer" *ngIf="section.type === SectionType.Standard">
      <div class="fl-button-group">
        <button mat-button color="primary-light" class="iconic" (click)="addElement(section, WidgetType.TextInput)" [disabled]="legacyFormService.savingForm && !legacyFormService.editable">
          <i class="material-icons">add_circle</i>
          Add Text Input
        </button>
        <button mat-button color="primary-light" class="drop-down" [matMenuTriggerFor]="addElementMenu" [disabled]="legacyFormService.savingForm && !legacyFormService.editable">
          <i class="material-icons">expand_more</i>
        </button>
      </div>

      <button mat-button color="primary-light" class="iconic" (click)="addContent(section)" [disabled]="legacyFormService.savingForm && !legacyFormService.editable">
        <i class="material-icons">add_circle</i>
        Add Content
      </button>
    </div>

    <mat-menu #addElementMenu="matMenu">
      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.TextInput)">
        <i class="material-icons">text_format</i>
        Text Input
      </button>
      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.TextArea)">
        <i class="material-icons">notes</i>
        Multiline Input
      </button>
      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.Password)">
        <i class="material-icons">more_horiz</i>
        Password Input
      </button>

      <div class="divider"></div>

      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.MultiSelect)">
        <i class="material-icons">arrow_drop_down_circle</i>
        Dropdown Select
      </button>
      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.Custom)">
        <i class="material-icons">dashboard</i>
        Custom Select
      </button>
      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.Radio)">
        <i class="material-icons">radio_button_checked</i>
        Radio
      </button>

      <div class="divider"></div>

      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.Checkbox)">
        <i class="material-icons">check_box</i>
        Checkbox
      </button>
      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.SlideToggle)">
        <i class="material-icons">toggle_on</i>
        Slide Toggle
      </button>

      <div class="divider"></div>

      <button mat-menu-item class="iconic" (click)="addElement(section,WidgetType.DatePicker)">
        <i class="material-icons">event</i>
        Date Picker
      </button>

      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.Document)">
        <i class="material-icons">cloud_upload</i>
        Document Upload
      </button>

      <div class="divider"></div>

      <button mat-menu-item class="iconic" (click)="addElement(section, WidgetType.Signature)">
        <i class="material-icons">gesture</i>
        Signature
      </button>
    </mat-menu>
  </mat-card>
</div>

<div class="mat-card-mock">
  <ng-container *ngIf="canAddSpecialSection; else addStandardSection">
    <div class="fl-button-group">
      <button
        mat-raised-button
        color="primary"
        class="iconic medium"
        (click)="addSection()"
      >
        <i class="material-icons">add_circle</i>
        Add New Section
      </button>

      <button
        mat-raised-button color="primary"
        class="drop-down"
        [matMenuTriggerFor]="addSectionMenu"
      >
        <i class="material-icons">expand_more</i>
      </button>
    </div>
  </ng-container>
  <ng-template #addStandardSection>
    <button
      mat-raised-button
      color="primary"
      class="iconic medium"
      (click)="addSection()"
    >
      <i class="material-icons">add_circle</i>
      Add New Section
    </button>
  </ng-template>


  <mat-menu #addSectionMenu="matMenu">
    <button mat-menu-item class="iconic" (click)="addSection(SectionType.InvestmentAccountSelection)" *ngIf="!this.hasInvestmentAccountSelectionForm">
      Add Investment Account Selection
    </button>
    <button mat-menu-item class="iconic" (click)="addSection(SectionType.InvestmentAccountSummary)" *ngIf="!this.hasInvestmentAccountSummary">
      Add Investment Account Summary
    </button>
    <button mat-menu-item class="iconic" (click)="addSection(SectionType.ProviderAccessRequest)" *ngIf="!this.hasProviderAccessRequest && legacyFormService.form.assignable_to === 'investment'">
      Add Provider Access Request
    </button>
  </mat-menu>
</div>
